import { ApiError } from "./api-error";
import { recordApiDiagnostic } from "./diagnostics";
import type { ApiRequest, ApiResponse, ApiTransport } from "./types";

export class ApiClient {
  constructor(private readonly transport: ApiTransport) {}

  async request<TData, TBody = undefined>(
    request: ApiRequest<TBody>,
  ): Promise<ApiResponse<TData>> {
    const startedAt = performance.now();
    const route = request.path.split(/[?#]/, 1)[0];
    recordApiDiagnostic({
      operation: request.operation,
      route,
      status: "started",
    });

    try {
      const response = await this.transport.send<TData, TBody>(request);
      recordApiDiagnostic({
        operation: request.operation,
        route,
        status: "succeeded",
        durationMs: performance.now() - startedAt,
        correlationId: response.correlationId,
      });
      return response;
    } catch (cause) {
      throw this.normalizeError(request, cause, startedAt, route);
    }
  }

  private normalizeError<TBody>(
    request: ApiRequest<TBody>,
    cause: unknown,
    startedAt: number,
    route: string,
  ): ApiError {
    const error = this.toApiError(request, cause);
    recordApiDiagnostic({
      operation: request.operation,
      route,
      status:
        error.kind === "outcome-unknown"
          ? "outcome-unknown"
          : error.kind === "cancelled"
            ? "cancelled"
            : "failed",
      durationMs: performance.now() - startedAt,
      correlationId: error.correlationId,
      errorCode: error.code,
      errorKind: error.kind,
    });
    return error;
  }

  private toApiError<TBody>(
    request: ApiRequest<TBody>,
    cause: unknown,
  ): ApiError {
    if (cause instanceof ApiError) {
      if (request.kind !== "command" || cause.kind !== "unavailable")
        return cause;
      return new ApiError(cause.message, {
        kind: "outcome-unknown",
        status: cause.status,
        code: cause.code,
        correlationId: cause.correlationId,
        problem: cause.problem,
        cause,
      });
    }

    if (cause instanceof DOMException && cause.name === "AbortError") {
      return new ApiError("API request cancelled", {
        kind: "cancelled",
        cause,
      });
    }

    return new ApiError("API response unavailable", {
      kind: request.kind === "command" ? "outcome-unknown" : "unavailable",
      cause,
    });
  }
}
